import React, { useState, useEffect } from 'react';
import { Dropdown, Menu } from 'antd';
import { ShareAltOutlined, AppstoreOutlined, TeamOutlined, RightOutlined } from '@ant-design/icons';
import styles from './HeaderMenu.module.css';
import { useDocument } from '../../../context/documentInfoContex';
import { useCanvas } from '../../../context/canvasContext';
import { useDarkMode } from '../../DarkModeToggle';
import { ShareModal } from '../../ShareModal/ShareModal';
import DocumentMembers from '../../Members/members';
import { apps, openApp } from '../../AppLauncher/apps';

/**
 * Hamburger for small screens: Members, Share, the theme toggle and the
 * app launcher as rows, in place of the desktop controls.
 */
const HeaderMenu = () => {
    const { documentInfo, isTemplateDocument } = useDocument();
    const { userInfo } = useCanvas();
    const [open, setOpen] = useState(false);

    // The menu is a portal; close it when the viewport grows past the breakpoint.
    useEffect(() => {
        const compact = window.matchMedia('(max-width: 767px)');
        const onChange = (e) => { if (!e.matches) setOpen(false); };
        compact.addEventListener('change', onChange);
        return () => compact.removeEventListener('change', onChange);
    }, []);

    const { dark, toggle: toggleDark } = useDarkMode();
    const members = (documentInfo?.members || []).filter((m) => m.id !== userInfo?.userId);
    const canShare = documentInfo?.createBy == userInfo?.userId && !isTemplateDocument;

    const row = (icon, label, extra) => (
        <div className={styles.menuItemContent}>
            <span className={styles.menuIconBox}>{icon}</span>
            <span className={styles.menuIconLabel}>{label}</span>
            {extra}
        </div>
    );
    const arrow = <RightOutlined className={styles.menuArrow} />;

    const menu = (
        <Menu className={styles.compactMenu}>
            {members.length > 0 && !isTemplateDocument && (
                <Menu.SubMenu
                    key="members"
                    popupClassName={styles.membersSubPopup}
                    title={row(<TeamOutlined />, 'Members', <><span className={styles.menuCount}>{members.length}</span>{arrow}</>)}
                >
                    <Menu.Item key="members-list" disabled>
                        <DocumentMembers />
                    </Menu.Item>
                </Menu.SubMenu>
            )}
            {canShare && (
                <Menu.Item key="share">
                    <ShareModal
                        viewMode="menu"
                        onMenuClick={() => setOpen(false)}
                        menuRow={row(<ShareAltOutlined />, 'Share')}
                    />
                </Menu.Item>
            )}
            <Menu.Divider className={styles.menuDivider} />
            <Menu.Item key="mode" onClick={() => { toggleDark(); setOpen(false); }}>
                {row(<span className={styles.menuModeIcon} aria-hidden="true">{dark ? '☀' : '☾'}</span>, dark ? 'Light mode' : 'Dark mode')}
            </Menu.Item>
            <Menu.SubMenu key="apps" popupClassName={styles.appsSubPopup} title={row(<AppstoreOutlined />, 'Apps', arrow)}>
                {apps.map((app) => (
                    <Menu.Item key={app.id} onClick={() => { openApp(app.url); setOpen(false); }}>
                        {row(<img src={app.icon} alt="" className={styles.menuIcon} />, app.name)}
                    </Menu.Item>
                ))}
            </Menu.SubMenu>
        </Menu>
    );

    return (
        <Dropdown overlay={menu} trigger={['click']} visible={open} onVisibleChange={setOpen} placement="bottomRight">
            <button
                type="button"
                className={`${styles.hamburgerBtn} ${open ? styles.hamburgerBtnActive : ''}`}
                aria-label="Menu"
                aria-expanded={open}
            >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
                    <path d="M4 7h16M4 12h16M4 17h16" />
                </svg>
            </button>
        </Dropdown>
    );
};

export default HeaderMenu;
